/**
 * Sentry verzögert laden — ohne frühe Fehler zu verlieren.
 *
 * @sentry/react wiegt rund 82 KB unkomprimiert und lag bislang im Haupt-Bundle,
 * das auf JEDER öffentlichen Landingpage geladen wird. Dort trägt es zum ersten
 * Rendern nichts bei; es verzögert es nur.
 *
 * Diese Datei hält deshalb zwei Dinge auseinander:
 *  - SOFORT (synchron, wenige Bytes): Listener, die Fehler ab der ersten
 *    Millisekunde in einen Puffer schreiben.
 *  - SPÄTER (dynamischer Import im Idle): das eigentliche SDK. Sobald es da ist,
 *    werden die gepufferten Fehler nachgemeldet.
 *
 * Ergebnis: gleiche Fehlerabdeckung, aber ~82 KB weniger auf dem kritischen Pfad.
 */

const DSN =
  'https://cd5748cd616753aa9c71a04eec8c37e1@o4511042342092800.ingest.de.sentry.io/4511042365554768';

/** Fehler, die auftraten, bevor das SDK geladen war. Begrenzt, damit ein Fehler-Sturm keinen Speicher frisst. */
const buffered = [];
const MAX_BUFFERED = 20;
let sentry = null;
let started = false;

function bufferError(error) {
  if (sentry) {
    sentry.captureException(error);
    return;
  }
  if (buffered.length < MAX_BUFFERED) buffered.push(error);
}

function onWindowError(event) {
  if (event.error) bufferError(event.error);
}

function onRejection(event) {
  if (event.reason) bufferError(event.reason);
}

/**
 * Meldet einen Fehler an Sentry — auch dann, wenn das SDK noch lädt.
 * Für Aufrufer außerhalb der globalen Handler (z. B. ErrorBoundary).
 */
export function captureException(error) {
  bufferError(error);
}

/**
 * Startet den Puffer sofort und lädt das SDK, sobald der Browser Luft hat.
 * Mehrfachaufrufe sind unschädlich.
 */
export function initSentryWhenIdle() {
  if (started) return;
  started = true;

  window.addEventListener('error', onWindowError);
  window.addEventListener('unhandledrejection', onRejection);

  const load = () => {
    import(/* webpackChunkName: "sentry" */ '@sentry/react')
      .then((Sentry) => {
        Sentry.init({
          dsn: DSN,
          environment: process.env.NODE_ENV || 'production',
          sendDefaultPii: true,
        });
        sentry = Sentry;

        // Was während des Ladens passiert ist, nachreichen.
        buffered.splice(0).forEach((err) => Sentry.captureException(err));

        // Ab hier übernimmt Sentrys eigene Instrumentierung.
        window.removeEventListener('error', onWindowError);
        window.removeEventListener('unhandledrejection', onRejection);
      })
      .catch(() => {
        // Sentry nicht ladbar (Adblocker, Offline). Kein Grund, die App zu stören —
        // die eigene Fehler-Pipeline in ErrorBoundary.jsx läuft davon unabhängig.
      });
  };

  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(load, { timeout: 4000 });
  } else {
    // Safari kennt requestIdleCallback nicht — nach dem Load-Event reicht auch.
    if (document.readyState === 'complete') setTimeout(load, 1500);
    else window.addEventListener('load', () => setTimeout(load, 1500), { once: true });
  }
}
